import React, { useContext, useEffect, useState, useMemo, useCallback } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Repeat,
  MessageCircle,
  Contact,
  Network,
  MessagesSquare,
  Users,
  Settings,
  Tag,
  Megaphone,
  CalendarClock,
  Image,
  Users2,
  Building2,
  Webhook,
  KanbanSquare,
  MessageSquareText,
  Workflow,
  BarChart3,
  ChevronDown,
  Megaphone as Aviso,
  ListChecks,
  Building,
  Receipt,
  Package,
  Puzzle,
  Shield,
  Sparkles,
  Activity,
  CircleDollarSign,
  BadgePercent,
  Bot,
  Lock,
  LifeBuoy,
  Plug,
  SlidersHorizontal,
  Quote,
  Radar,
  // Seção Redes sociais (Zernio) — Megaphone e BarChart3 já entram acima.
  AtSign,
  FileText,
  MessageSquare,
  Star,
  Target,
  Brain,
  Eye,
  EyeOff,
} from "lucide-react";

import { cn } from "../lib/utils";
import { lerPreferencia, gravarPreferencia } from "../services/preferencias";
import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import useModules from "../hooks/useModules";
import { Can } from "../components/Can";
import usePermissions from "../hooks/usePermissions";
import { listarContextoRecente } from "../services/recentContext";

export function NavItem({
  to,
  icon: Icon,
  label,
  badge,
  onClick,
  collapsed,
  // Recurso fora do plano. Some do menu era pior do que aparecer apagado:
  // quem não vê o item não sabe que ele existe, e não tem o que pedir.
  foraDoPlano,
  exato,
}) {
  return (
    <NavLink
      to={to}
      // `exato` obriga o caminho a bater inteiro. Vale para o item cujo
      // endereço é prefixo de outro ("/zernio" e "/zernio/publicacoes"), que
      // sem isso fica aceso junto com o filho. Não pode ser o padrão: Tickets
      // aponta para "/tickets" e precisa continuar aceso em "/tickets/123".
      end={exato}
      onClick={onClick}
      title={
        foraDoPlano
          ? `${label} — não está no seu plano. Fale com quem administra a conta para liberar.`
          : undefined
      }
      className={({ isActive }) =>
        cn(
          "group flex items-center gap-3 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
          "hover:bg-accent hover:text-accent-foreground",
          isActive ? "bg-primary/10 text-primary" : "text-muted-foreground",
          foraDoPlano && "opacity-50"
        )
      }
    >
      <span className="relative flex h-5 w-5 shrink-0 items-center justify-center">
        <Icon className="h-[18px] w-[18px]" />
        {badge && (
          <span className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-destructive text-[9px] font-bold text-destructive-foreground">
            !
          </span>
        )}
      </span>
      {!collapsed && (
        <>
          <span className="truncate">{label}</span>
          {foraDoPlano && <Lock className="ml-auto h-3 w-3 shrink-0" />}
        </>
      )}
    </NavLink>
  );
}

function NavSection({ id, label, collapsed, fechadaPorPadrao, children }) {
  // O "v2" zera a preferência guardada uma vez. Configurações nascia fechada e
  // engolia Conexões, Filas e Usuários; quem já tinha o "fechado" salvo
  // continuaria sem ver mesmo depois da mudança. Depois deste reset, a escolha
  // de cada um volta a valer normalmente.
  const storageKey = `navSection:v2:${id}`;
  const [open, setOpen] = useState(() => {
    const stored = lerPreferencia(storageKey, null);
    // Hoje só a Gestão SaaS nasce fechada: são telas de dono do produto, não
    // do dia a dia de quem atende.
    if (stored === null) return !fechadaPorPadrao;
    return stored === true;
  });

  // ── Hooks EM NÍVEL SUPERIOR (fora de qualquer early return) ──
  // A regra dos Hooks React é: eles SÓ PODEM ser chamados no corpo da
  // função/componente, em quantidade FIXA por render. Se estiverem atrás
  // de um `if (collapsed) return ...` ou de uma função invocada só quando
  // `!collapsed`, o React vê "primeiro render = 0 hooks, segundo render = N
  // hooks" → erro "Rendered more hooks than previous render" e tela
  // quebrava APÓS o login (essa era a causa da crash reportada).
  const menuCtx = useContext(AdaptiveMenuContext);
  const lista = useMemo(() => {
    const arr = [];
    React.Children.forEach(children, (child, idx) => {
      if (!React.isValidElement(child)) return;
      arr.push({ child, idx });
    });
    return arr;
  }, [children]);
  const storageColapsadosKey = `navSection:v3:adaptive:${id}`;
  const [mostrarTodos, setMostrarTodos] = useState(() => {
    const s = lerPreferencia(storageColapsadosKey, null);
    return s === true;
  });

  // --- Interface Adaptativa FASE 3 ---
  // Dentro de cada seção, itens MENOS usados (sem touch recente na FASE 2
  // UserRecentContexts) são colapsados num "Ver mais…" expansível.
  //
  // REGRAS CRÍTICAS VERBATIM:
  // • NENHUM item NUNCA é removido da sidebar. Permissões continuam sendo a
  //   regra de segurança. A adaptação é SÓ UX (ordem + colapsar).
  // • Itens com badge, foraDoPlano=true ou primeira posição fixa ("fixo")
  //   NUNCA são colapsados — o aviso de conexão OFFLINE tem que ficar visível.
  // • Seção pequena (<=5 itens visíveis): não colapsa nada.
  const toggleMostrarTodos = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setMostrarTodos((prev) => {
      gravarPreferencia(storageColapsadosKey, !prev);
      return !prev;
    });
  };

  // Agora que temos o contexto e a lista sempre computados no topo, a função
  // abaixo é 100% PURA (zero hooks) — pode ser chamada condicionalmente
  // sem quebrar as regras.
  const { itensFixos, itensMenosUsados, temAdaptacao } = separarItensAdaptativos(
    lista,
    menuCtx.sinais,
    menuCtx.carregando,
    mostrarTodos
  );

  const toggle = (e) => {
    // não deixa o clique do cabeçalho fechar o drawer mobile
    e.stopPropagation();
    setOpen((prev) => {
      gravarPreferencia(storageKey, !prev);
      return !prev;
    });
  };

  if (collapsed) {
    return (
      <>
        <div className="my-2 h-px bg-border" />
        {children}
      </>
    );
  }

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center justify-between rounded-md px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70 transition-colors hover:text-foreground"
      >
        <span>{label}</span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 transition-transform duration-200",
            !open && "-rotate-90"
          )}
        />
      </button>
      <div
        className={cn(
          "ml-[18px] mt-0.5 flex flex-col gap-0.5 overflow-hidden border-l border-border/60 pl-1.5 transition-all",
          !open && "hidden"
        )}
      >
        {itensFixos}
        {temAdaptacao && (
          <>
            {!mostrarTodos && (
              <div className="relative flex items-center justify-center py-1">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-border/60 to-transparent" />
                <button
                  type="button"
                  onClick={toggleMostrarTodos}
                  className="mx-2 inline-flex items-center gap-1 rounded-full border border-border/60 bg-card px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                >
                  <Eye className="h-3 w-3" /> Ver mais {itensMenosUsados?.length || 0}
                </button>
                <div className="flex-1 h-px bg-gradient-to-l from-transparent via-border/60 to-transparent" />
              </div>
            )}
            {mostrarTodos && (
              <>
                {itensMenosUsados}
                <button
                  type="button"
                  onClick={toggleMostrarTodos}
                  className="mt-0.5 inline-flex w-max items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                >
                  <EyeOff className="h-3 w-3" /> Ver menos
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// Contexto de Interface Adaptativa (FASE 3)
//
// Reutiliza 100% a tabela UserRecentContexts + endpoint /recent-contexts da
// FASE 2 como fonte de sinais de uso. NENHUM endpoint novo, NENHUM migration
// nova, NENHUM model novo, NENHUM socket novo.
//
// Ideia: quanto mais recente o touch da entidade, maior o score. Itens com
// score maior aparecem primeiro na seção; os MENOS usados (abaixo do limiar
// de 5 itens fixos visíveis) são agrupados num "Ver mais…" expansível.
//
// REGRAS NÃO NEGOCIÁVEIS (VERBATIM spec.md FASE 3):
// 1. NUNCA remove telas do menu. Permissões e módulos continuam a regra de
//    segurança — tudo que o usuário tem direito continua aparecendo, só que
//    os menos usados ficam atrás de um clique.
// 2. Seção pequena (<=5 itens visíveis): NÃO colapsa, NÃO mostra "Ver mais".
// 3. Itens com badge (ex.: conexão WhatsApp offline) OU foraDoPlano (cadeado)
//    NUNCA são colapsados — o aviso tem que ficar visível de relance.
// 4. Drawer colapsado (modo ícone): ignora adaptação (mostra ícones todos).
// =========================================================================
const AdaptiveMenuContext = React.createContext({
  sinais: new Map(),
  carregando: true,
});

const LIMIAR_ITENS_FIXOS = 5;

function rotaBaseParaEntidade(caminho) {
  if (!caminho || typeof caminho !== "string") return [];
  const limpo = caminho.replace(/^\/+/, "").split(/[/#?]/)[0] || "";
  if (!limpo) return ["dashboard", "home"];
  const partes = limpo.split("/");
  const primeiro = partes[0];
  switch (primeiro) {
    case "":
    case "dashboard":
      return ["dashboard", "home"];
    case "tickets":
      return ["ticket", "tickets"];
    case "contacts":
    case "quickAnswers":
    case "media-library":
      return ["contact", primeiro === "quickAnswers" ? "quickAnswer" : primeiro, "contacts"];
    case "tarefas":
    case "kanban":
      return ["task", "tarefa", primeiro];
    case "flows":
      return ["flow", "flows"];
    case "scheduled-messages":
      return ["scheduledMessage", "agendamento", "schedules"];
    case "ai":
    case "intelligence":
    case "settings-intelligence":
      return ["ai", "intelligence", primeiro];
    case "campaigns":
    case "contact-lists":
    case "campaigns-phrases":
    case "campaigns-config":
      return ["campaign", "campanhas", primeiro];
    case "operational-panel":
    case "reports":
      return ["dashboard", "reports", primeiro];
    case "internal-chat":
      return ["internalChat", "chat", primeiro];
    case "announcements":
    case "helps":
      return ["ticketNote", "announcement", primeiro];
    case "zernio":
      return ["campaign", "zernio", ...partes.slice(0, 2).filter(Boolean)];
    case "connections":
    case "Queues":
    case "queues":
    case "tags":
    case "users":
    case "roles":
    case "teams":
    case "departments":
    case "webhooks":
    case "queue-integrations":
    case "modelos-whatsapp":
    case "messages-api":
    case "Settings":
    case "global-config":
    case "financeiro":
      return ["ticket", primeiro.toLowerCase()];
    case "saas":
    case "companies":
    case "plans":
    case "modules":
    case "invoices":
      return ["campaign", "saas", primeiro];
    default:
      return [primeiro];
  }
}

function entidadeMatchaRota(sinalEntityType, caminhoItem) {
  const entidades = rotaBaseParaEntidade(caminhoItem);
  const tipo = String(sinalEntityType || "").toLowerCase();
  return entidades.some((e) => String(e || "").toLowerCase() === tipo);
}

function calcularScoreItem(item, sinaisPorRota, indexOriginal) {
  // Preservação de posição original: primeiros itens têm um bônus pequeno para
  // o menu não ficar embaralhado demais quando o usuário ainda não usou nada.
  let score = (1 / (1 + indexOriginal)) * 0.15;
  const sinais = sinaisPorRota || [];
  const matches = sinais.filter((row) =>
    entidadeMatchaRota(row.entityType, item.to)
  );
  if (matches.length > 0) {
    matches.forEach((_row, i) => {
      // Cada match recente multiplica: 1.0 para o mais novo, 0.95^1 para o
      // segundo, etc. Decaimento suave entre os 20 recentes.
      const pesoRecencia = Math.pow(0.92, i);
      score += 2.0 * pesoRecencia;
    });
  }
  return { score, matches };
}

/**
 * Versão PURA (zero hooks) da antiga `usarAdaptacaoNaLista`.
 *
 * Como `usarAdaptacaoNaLista` chamava `useContext` + `useMemo` DENTRO do corpo
 * de `NavSection` SOMENTE quando `!collapsed` (havia um `if (collapsed) return
 * children` antes), a quantidade de hooks mudava por render → React crashava
 * com "Rendered more hooks than during the previous render" após o login
 * (o drawer mudava de collapsed → expandido ou vice-versa mudava qtd hooks).
 *
 * Solução: TODOS os hooks foram promovidos para o TOPO de `NavSection`, e o
 * cálculo aqui recebe os valores já prontos → função pura.
 */
function separarItensAdaptativos(lista, sinais, carregando, mostrarTodos) {
  const total = Array.isArray(lista) ? lista.length : 0;
  // Apenas 5 itens ou menos → nenhuma adaptação.
  if (total <= LIMIAR_ITENS_FIXOS || carregando || mostrarTodos) {
    return {
      itensFixos: (lista || []).map((l) => l.child),
      itensMenosUsados: [],
      temAdaptacao: false,
    };
  }

  const scores = (lista || []).map(({ child, idx }) => {
    const itemProps = child.props || {};
    const ehFixoPorRegra =
      itemProps.badge === true || itemProps.foraDoPlano === true;
    if (ehFixoPorRegra) {
      return {
        child,
        idx,
        score: 999_999 + (LIMIAR_ITENS_FIXOS - idx) * 10,
        matches: [],
        fixoForcado: true,
      };
    }
    const r = calcularScoreItem(itemProps, sinais || [], idx);
    return { child, idx, score: r.score, matches: r.matches, fixoForcado: false };
  });

  const fixosForcados = scores.filter((s) => s.fixoForcado);
  const naoFixosOrdenados = scores
    .filter((s) => !s.fixoForcado)
    .sort((a, b) => b.score - a.score);

  const vagasRestantes = Math.max(0, LIMIAR_ITENS_FIXOS - fixosForcados.length);
  const fixosPorScore = naoFixosOrdenados.slice(0, vagasRestantes);

  const idsFixos = new Set(
    fixosForcados.concat(fixosPorScore).map((s) => s.idx)
  );

  const fixosFinal = [];
  const menosFinal = [];
  scores.forEach((s) => {
    if (idsFixos.has(s.idx)) fixosFinal.push(s.child);
    else menosFinal.push(s.child);
  });

  if (menosFinal.length === 0) {
    return {
      itensFixos: fixosFinal,
      itensMenosUsados: [],
      temAdaptacao: false,
    };
  }

  return {
    itensFixos: fixosFinal,
    itensMenosUsados: menosFinal,
    temAdaptacao: true,
  };
}

function AdaptiveMenuProvider({ children }) {
  const { user } = useContext(AuthContext);
  const [estado, setEstado] = useState({ sinais: [], carregando: true });

  const carregar = useCallback(async () => {
    if (!user?.id) {
      setEstado({ sinais: [], carregando: false });
      return;
    }
    try {
      // Reusa o endpoint oficial da Fase 2, com limit 20 (máximo permitido).
      const { rows = [] } = await listarContextoRecente({ limit: 20 });
      setEstado({ sinais: rows, carregando: false });
    } catch {
      // Se falhar por qualquer motivo (offline, timeout, API caiu) → cai no
      // fallback: nenhum sinal. Menu continua mostrando a ordem original,
      // só sem adaptação. NUNCA quebra a sidebar.
      setEstado({ sinais: [], carregando: false });
    }
  }, [user?.id]);

  useEffect(() => {
    carregar();
    // Refresco leve a cada 90 segundos — não fazemos polling pesado.
    const t = setInterval(carregar, 90_000);
    return () => clearInterval(t);
  }, [carregar]);

  const valor = useMemo(() => ({
    sinais: estado.sinais,
    carregando: estado.carregando,
  }), [estado.sinais, estado.carregando]);

  return (
    <AdaptiveMenuContext.Provider value={valor}>
      {children}
    </AdaptiveMenuContext.Provider>
  );
}

// O menu como dado, não como JSX.
//
// A barra de cima (TopNav) precisa exatamente dos mesmos itens, nas mesmas
// seções, com as mesmas permissões e os mesmos módulos do plano — só que em
// menus suspensos. Manter duas listas em dois arquivos garante que um dia elas
// divergem: entra uma tela nova na lateral e ela não existe no topo. Por isso
// a fonte é esta função; quem desenha apenas percorre o que ela devolve.
//
// Cada seção: { id, label, fechadaPorPadrao?, itens: [{ to, icon, label,
// badge?, foraDoPlano? }] }. Seção sem `label` é item solto, fora de grupo.
export function useSecoesDoMenu() {
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  // Esconde do menu o que a empresa nao contratou.
  const { temModulo } = useModules();
  const { temPermissao } = usePermissions();
  const [connectionWarning, setConnectionWarning] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        setConnectionWarning(offlineWhats.length > 0);
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  // `Can` é um componente, mas é função pura sem hook: chamada direta ela
  // devolve o booleano que precisamos aqui. É a mesma regra de sempre
  // (src/rules), sem uma segunda checagem de permissão para manter em dia.
  const podeAdministrar = Can({
    role: user.profile,
    perform: "drawer-admin-items:view",
    yes: () => true,
    no: () => false,
  });

  const secoes = [];

  // Dashboard sozinho no topo: é a tela inicial, não pertence a nenhum grupo.
  secoes.push({
    id: "inicio",
    label: null,
    itens: [
      ...(temPermissao("dashboard:view")
        ? [{ to: "/", icon: LayoutDashboard, label: "Dashboard" }]
        : []),
      { to: "/tarefas", icon: ListChecks, label: "Tarefas", foraDoPlano: !temModulo("tasks") && !temModulo("kanban") },
    ],
  });

  // Atendimento: a conversa, o contato e o material que a equipe reusa
  // ao responder.
  secoes.push({
    id: "atendimento",
    label: "Atendimento",
    itens: [
      {
        to: "/tickets",
        icon: MessageCircle,
        label: i18n.t("mainDrawer.listItems.tickets"),
      },
      {
        to: "/contacts",
        icon: Contact,
        label: i18n.t("mainDrawer.listItems.contacts"),
      },
      {
        to: "/quickAnswers",
        icon: MessagesSquare,
        label: i18n.t("mainDrawer.listItems.quickAnswers"),
      },
      {
        to: "/media-library",
        icon: Image,
        label: "Biblioteca de mídias",
        foraDoPlano: !temModulo("mediaLibrary"),
      },
    ],
  });

  // Automação: tudo que fala com o cliente sem ninguém digitar.
  // As mensagens agendadas vieram para cá — elas são envio automático,
  // não material de apoio.
  // Facebook e Instagram estão fora do menu por enquanto: o token da Meta
  // expirou e o app ainda não tem o App Secret para renovar. A tela e a
  // integração continuam no código — é só devolver o item e a rota em
  // routes/index.js quando as credenciais estiverem prontas.
  secoes.push({
    id: "automacao",
    label: "Automação",
    itens: [
      ...(temPermissao("flows:manage") || temPermissao("system:integrations")
        ? [{
            to: "/flows",
            icon: Workflow,
            label: "Fluxo de conversa",
            foraDoPlano: !temModulo("flows"),
          }]
        : []),
      {
        to: "/scheduled-messages",
        icon: CalendarClock,
        label: "Agendamentos",
        foraDoPlano: !temModulo("schedules"),
      },
      { to: "/ai", icon: Sparkles, label: "Talk.Ai" },
    ],
  });

  // Vender: prospecção e disparo em massa moram juntos porque a campanha
  // é a ferramenta de quem trabalha os leads.
  secoes.push({
    id: "vendas",
    label: "Vendas",
    itens: [
      // Leads, funil e tarefas são o mesmo trabalho visto de três ângulos:
      // moram juntos no Kanban, em abas.
      {
        to: "/kanban",
        icon: KanbanSquare,
        label: "Kanban",
        foraDoPlano: !temModulo("crm") && !temModulo("kanban"),
      },
      {
        to: "/campaigns",
        icon: Megaphone,
        label: "Campanhas",
        foraDoPlano: !temModulo("campaigns"),
      },
      {
        to: "/contact-lists",
        icon: ListChecks,
        label: "Listas de Contatos",
        foraDoPlano: !temModulo("campaigns"),
      },
      {
        to: "/campaigns-phrases",
        icon: Quote,
        label: "Fluxo de Campanha",
        foraDoPlano: !temModulo("campaigns"),
      },
      {
        to: "/campaigns-config",
        icon: SlidersHorizontal,
        label: "Config. de campanhas",
        foraDoPlano: !temModulo("campaigns"),
      },
      // Prospecção: encontrar empresas que ainda não são clientes. Vem depois
      // das campanhas porque alimenta elas — a lista de contatos que sai daqui
      // é o que uma campanha dispara.
      {
        to: "/prospect",
        icon: Radar,
        label: "Prospecção",
        foraDoPlano: !temModulo("prospeccao"),
      },
      {
        to: "/prospect/config",
        icon: SlidersHorizontal,
        label: "Config. de prospecção",
        foraDoPlano: !temModulo("prospeccao"),
      },
    ],
  });

  // Acompanhar: os números do que já aconteceu.
  secoes.push({
    id: "analise",
    label: "Acompanhamento",
    itens: [
      ...(temPermissao("dashboard:metrics") || temPermissao("supervision:monitor")
        ? [{ to: "/operational-panel", icon: Activity, label: "Painel" }]
        : []),
      ...(temModulo("reports") && temPermissao("dashboard:reports")
        ? [{
            to: "/reports",
            icon: BarChart3,
            label: "Relatórios",
          }]
        : temModulo("reports")
          ? [{
              to: "/reports",
              icon: BarChart3,
              label: "Relatórios",
              foraDoPlano: false,
              // Mostra mas esmaecido se for plano ativo mas sem permissão —
              // usamos esconder quando é pura questão de cargo (abaixo).
            }]
          : []),
      ...(temPermissao("dashboard:metrics") || temPermissao("dashboard:reports") || temPermissao("intelligence:use")
        ? [{
            to: "/intelligence",
            icon: Brain,
            label: "Inteligência",
            foraDoPlano: !temModulo("intelligence"),
          }]
        : []),
    ],
  });

  // Equipe: conversa interna, não com o cliente.
  secoes.push({
    id: "equipe",
    label: "Equipe",
    itens: [
      {
        to: "/internal-chat",
        icon: MessageSquareText,
        label: "Chat Interno",
        foraDoPlano: !temModulo("internalChat"),
      },
      { to: "/announcements", icon: Aviso, label: "Informativos" },
      { to: "/helps", icon: LifeBuoy, label: "Ajuda" },
    ],
  });

  // Redes sociais: tudo que passa pela Zernio. Seção própria porque são sete
  // trabalhos diferentes — atender, publicar, responder comentário, responder
  // avaliação — e não variações de uma tela só.
  // Redes sociais é módulo vendido à parte: quem não o contratou vê os itens
  // com cadeado, como acontece com campanhas e fluxos.
  const semZernio = !temModulo("zernio");

  secoes.push({
    id: "zernio",
    label: "Redes sociais",
    fechadaPorPadrao: true,
    itens: [
      // `exato` porque "/zernio" é prefixo das outras seis.
      {
        to: "/zernio",
        icon: Plug,
        label: "Contas",
        exato: true,
        foraDoPlano: semZernio,
      },
      {
        to: "/zernio/publicacoes",
        icon: Megaphone,
        label: "Publicações",
        foraDoPlano: semZernio,
      },
      {
        to: "/zernio/comentarios",
        icon: MessageSquare,
        label: "Comentários",
        foraDoPlano: semZernio,
      },
      {
        to: "/zernio/mencoes",
        icon: AtSign,
        label: "Menções",
        foraDoPlano: semZernio,
      },
      {
        to: "/zernio/avaliacoes",
        icon: Star,
        label: "Avaliações",
        foraDoPlano: semZernio,
      },
      {
        to: "/zernio/analises",
        icon: BarChart3,
        label: "Análises",
        foraDoPlano: semZernio,
      },
      {
        to: "/zernio/anuncios",
        icon: Target,
        label: "Anúncios",
        foraDoPlano: semZernio,
      },
    ],
  });

  // Configurações: o que se ajusta uma vez e esquece. Nasce fechada —
  // são nove linhas que empurravam o dia a dia para fora da tela.
  // Conexões saiu do topo: é configuração, mesmo sendo importante.
  if (podeAdministrar) {
    secoes.push({
      id: "configuracoes",
      label: "Configurações",
      itens: [
        {
          to: "/connections",
          icon: Repeat,
          label: i18n.t("mainDrawer.listItems.connections"),
          badge: connectionWarning,
        },
        {
          to: "/Queues",
          icon: Network,
          label: i18n.t("mainDrawer.listItems.queues"),
        },
        { to: "/tags", icon: Tag, label: i18n.t("mainDrawer.listItems.tags") },
        {
          to: "/users",
          icon: Users,
          label: i18n.t("mainDrawer.listItems.users"),
        },
        { to: "/roles", icon: Shield, label: "Cargos e permissões" },
        { to: "/teams", icon: Users2, label: "Equipes" },
        { to: "/departments", icon: Building2, label: "Departamentos" },
        {
          to: "/webhooks",
          icon: Webhook,
          label: "Webhooks",
          foraDoPlano: !temModulo("webhooks"),
        },
        { to: "/queue-integrations", icon: Bot, label: "Integrações" },
        // Modelos do WhatsApp oficial: sem eles não há campanha nem retomada
        // de conversa passadas 24 h.
        { to: "/modelos-whatsapp", icon: FileText, label: "Modelos WhatsApp" },
        { to: "/messages-api", icon: Plug, label: "API" },
        {
          to: "/settings-intelligence",
          icon: Sparkles,
          label: "Inteligência",
          foraDoPlano: !temModulo("intelligence"),
        },
        // Ajustes da plataforma inteira: a rota é isSuperAdmin, então
        // mostrar para o cliente só renderia um 403.
        ...(Number(user.companyId) === 1
          ? [
              {
                to: "/global-config",
                icon: SlidersHorizontal,
                label: "Configurações globais",
              },
            ]
          : []),
        // A empresa operadora já tem o painel financeiro do SaaS; para ela
        // esta tela seria a própria fatura, que não existe.
        ...(Number(user.companyId) !== 1
          ? [
              {
                to: "/financeiro",
                icon: CircleDollarSign,
                label: "Financeiro",
              },
            ]
          : []),
        {
          to: "/Settings",
          icon: Settings,
          label: i18n.t("mainDrawer.listItems.settings"),
        },
        // Registros imutáveis de auditoria (quem excluiu atendimento/contato etc).
        // APENAS administrador vê (supervisor e user NÃO — segurança maior).
        ...(String(user.profile || "").toLowerCase() === "admin"
          ? [
              {
                to: "/audit-logs",
                icon: FileText,
                label: "Logs de Auditoria",
              },
            ]
          : []),
      ],
    });
  }

  // Gestão do SaaS: só o admin da empresa operadora (id 1) enxerga.
  if (user.profile === "admin" && Number(user.companyId) === 1) {
    secoes.push({
      id: "saas",
      label: "Gestão SaaS",
      fechadaPorPadrao: true,
      itens: [
        { to: "/saas/finance", icon: CircleDollarSign, label: "Painel financeiro" },
        { to: "/companies", icon: Building, label: "Empresas" },
        { to: "/plans", icon: Package, label: "Planos" },
        { to: "/modules", icon: Puzzle, label: "Módulos" },
        { to: "/saas/commercial", icon: BadgePercent, label: "Comercial" },
        { to: "/invoices", icon: Receipt, label: "Faturas" },
      ],
    });
  }

  return secoes;
}

const MainListItems = ({ drawerClose, collapsed }) => {
  const secoes = useSecoesDoMenu();

  return (
    <AdaptiveMenuProvider>
      <nav className="flex flex-col gap-0.5 px-2 pb-6" onClick={drawerClose}>
        {secoes.map((secao) =>
          secao.label ? (
            <NavSection
              key={secao.id}
              id={secao.id}
              label={secao.label}
              collapsed={collapsed}
              fechadaPorPadrao={secao.fechadaPorPadrao}
            >
              {secao.itens.map((item) => (
                <NavItem key={item.to} {...item} collapsed={collapsed} />
              ))}
            </NavSection>
          ) : (
            secao.itens.map((item) => (
              <NavItem key={item.to} {...item} collapsed={collapsed} />
            ))
          )
        )}
      </nav>
    </AdaptiveMenuProvider>
  );
};

export default MainListItems;
